import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface Props {
  forumId: string;
  initialRating?: number;
  totalRatings?: number;
  darkMode: boolean;
  onRatingChange?: (newRating: number) => void;
}

const ForumRating: React.FC<Props> = ({
  forumId,
  initialRating = 0,
  totalRatings = 0,
  darkMode,
  onRatingChange
}) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserRating();
    }
  }, [user, forumId]);

  const fetchUserRating = async () => {
    try {
      const { data, error } = await supabase
        .from('forum_ratings')
        .select('rating')
        .eq('forum_id', forumId)
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setUserRating(data.rating);
        setRating(data.rating);
      } else {
        setUserRating(0);
        setRating(0);
      }
    } catch (error) {
      console.error('Error fetching user rating:', error);
      // Reset ratings to 0 if there's an error
      setUserRating(0);
      setRating(0);
    }
  };

  const handleRating = async (value: number) => {
    if (!user) {
      toast.error('Puan vermek için giriş yapmalısınız');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase
        .from('forum_ratings')
        .upsert({
          forum_id: forumId,
          user_id: user.id,
          rating: value
        }, {
          onConflict: 'forum_id,user_id'
        });

      if (error) throw error;

      setRating(value);
      setUserRating(value);
      if (onRatingChange) {
        onRatingChange(value);
      }
      toast.success('Puanınız kaydedildi');
    } catch (error) {
      console.error('Error rating forum:', error);
      toast.error('Puan verilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`flex flex-col items-center sm:items-end shrink-0 ${
        darkMode ? 'text-white' : 'text-gray-900'
      }`}
    >
      <div className="flex items-center gap-0.5 sm:gap-1 mb-2">
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            disabled={loading}
            onClick={() => handleRating(value)}
            onMouseEnter={() => setHoveredRating(value)}
            onMouseLeave={() => setHoveredRating(0)}
            className={`p-1 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'}`}
          >
            <Star
              size={24}
              className={`transition-colors ${
                (hoveredRating ? value <= hoveredRating : value <= (rating || initialRating))
                  ? 'fill-yellow-400 text-yellow-400'
                  : darkMode
                  ? 'text-gray-600'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {totalRatings > 0 ? (
          <span>{initialRating.toFixed(1)} ({totalRatings} oy)</span>
        ) : (
          <span>Henüz puan verilmemiş</span>
        )}
      </div>
      {userRating > 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Sizin puanınız: {userRating}
        </div>
      )}
    </div>
  );
};

export default ForumRating;